import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, fmtMoney, Badge } from "@/components/ui-kit";
import ClosestTargets from "@/components/ClosestTargets";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area,
  BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, AlertTriangle, Stethoscope, Building2,
  Pill, Users, Activity, ShoppingCart,
} from "lucide-react";

const COLORS = ["#1d4ed8", "#0ea5e9", "#0f172a", "#f59e0b", "#10b981", "#8b5cf6"];

function KPI({ label, value, icon: Icon, trend, color = "blue", testid }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-md grid place-items-center ${colorMap[color]}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        {trend && <Badge color={trend > 0 ? "green" : "red"}>{trend > 0 ? "▲" : "▼"} {Math.abs(trend)}%</Badge>}
      </div>
      <div className="mt-4 overline">{label}</div>
      <div className="metric-value mt-1">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [repData, setRepData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (user?.role === "sales_rep") {
          const [exec, rep] = await Promise.all([
            api.get("/dashboard/executive"),
            api.get("/dashboard/rep"),
          ]);
          setData(exec.data);
          setRepData(rep.data);
        } else {
          const exec = await api.get("/dashboard/executive");
          setData(exec.data);
        }
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [user]);

  if (!data) return <div className="text-slate-500">{t("loading")}</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-4 border-b border-slate-200">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tighter text-slate-900" style={{ fontFamily: "Manrope" }}>
            {t("dashboard")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {new Date().toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", {
              weekday: "long", year: "numeric", month: "long", day: "numeric"
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge color="blue">{user?.name}</Badge>
          <Badge color="slate">{t(user?.role)}</Badge>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI testid="kpi-daily-sales" label={t("dailySales")} value={`${fmtMoney(k.daily_sales)} ${t("egp")}`} icon={TrendingUp} color="blue" />
        <KPI testid="kpi-monthly-sales" label={t("monthlySales")} value={`${fmtMoney(k.monthly_sales)} ${t("egp")}`} icon={ShoppingCart} color="blue" />
        <KPI testid="kpi-daily-collections" label={t("dailyCollections")} value={`${fmtMoney(k.daily_collections)} ${t("egp")}`} icon={Wallet} color="emerald" />
        <KPI testid="kpi-monthly-collections" label={t("monthlyCollections")} value={`${fmtMoney(k.monthly_collections)} ${t("egp")}`} icon={Wallet} color="emerald" />
        <KPI testid="kpi-outstanding" label={t("outstanding")} value={`${fmtMoney(k.outstanding_balance)} ${t("egp")}`} icon={TrendingDown} color="amber" />
        <KPI testid="kpi-expiry-alerts" label={t("expiryAlerts")} value={k.expiry_alert_count} icon={AlertTriangle} color="red" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI testid="stat-doctors" label={t("totalDoctors")} value={k.doctors} icon={Stethoscope} color="slate" />
        <KPI testid="stat-pharmacies" label={t("totalPharmacies")} value={k.pharmacies} icon={Building2} color="slate" />
        <KPI testid="stat-products" label={t("totalProducts")} value={k.products} icon={Pill} color="slate" />
        <KPI testid="stat-reps" label={t("totalReps")} value={k.reps} icon={Users} color="slate" />
        <KPI testid="stat-visits-30" label={t("visits30")} value={k.visits_30d} icon={Activity} color="slate" />
        <KPI testid="stat-orders-30" label={t("orders30")} value={k.orders_30d} icon={ShoppingCart} color="slate" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>
              {t("salesTrend")}
            </h3>
            <Badge color="blue">14 days</Badge>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.sales_series}>
              <defs>
                <linearGradient id="salesG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1d4ed8" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#1d4ed8" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="collG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="sales" stroke="#1d4ed8" fill="url(#salesG)" strokeWidth={2} />
              <Area type="monotone" dataKey="collections" stroke="#10b981" fill="url(#collG)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Manrope" }}>
            {t("salesByProduct")}
          </h3>
          {data.sales_by_product.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.sales_by_product} dataKey="amount" nameKey="product" cx="50%" cy="50%" outerRadius={90} label>
                  {data.sales_by_product.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-slate-400 text-sm">No sales yet</div>
          )}
        </Card>
      </div>

      {/* Top performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Manrope" }}>
            {t("topPharmacies")}
          </h3>
          <div className="space-y-3">
            {data.top_pharmacies.map((p, i) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded bg-blue-50 text-blue-700 grid place-items-center font-bold text-xs">
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.city} · {p.classification}</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-blue-700">{fmtMoney(p.total_orders)} {t("egp")}</div>
              </div>
            ))}
            {data.top_pharmacies.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No data yet</div>}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Manrope" }}>
            {t("topProducts")}
          </h3>
          <div className="space-y-3">
            {data.top_products.map((p, i) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded grid place-items-center text-white font-bold text-xs" style={{ backgroundColor: p.image_color || "#1d4ed8" }}>
                    {p.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">SKU: {p.sku} · Stock: {p.current_stock}</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-slate-700">{p.total_sold} sold</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Rep performance + market alerts */}
      {(user?.role === "admin" || user?.role === "supervisor") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Manrope" }}>
              {t("repPerformance")}
            </h3>
            {data.rep_performance.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.rep_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={10} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="visits" fill="#0ea5e9" />
                  <Bar dataKey="sales" fill="#1d4ed8" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-400 text-sm">No rep activity yet</div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Manrope" }}>
              {t("marketAlerts")}
            </h3>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {data.market_alerts.map((m) => (
                <div key={m.id} className="flex items-start gap-3 border-b border-slate-100 pb-2">
                  <Badge color={m.severity === "high" ? "red" : m.severity === "medium" ? "amber" : "slate"}>
                    {m.intel_type}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{m.competitor_brand}</div>
                    <div className="text-xs text-slate-500 truncate">{m.description}</div>
                  </div>
                </div>
              ))}
              {data.market_alerts.length === 0 && (
                <div className="text-sm text-slate-400 py-6 text-center">No competitor activity logged</div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Closest unvisited targets - high-leverage widget */}
      <ClosestTargets/>

      {/* Sales rep personal */}
      {repData && (
        <Card className="p-6 bg-blue-700 text-white">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "Manrope" }}>My Personal KPIs (30d)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div><div className="text-xs uppercase tracking-widest text-blue-200">Visits Today</div><div className="text-2xl font-extrabold">{repData.visits_today}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-blue-200">Visits 30d</div><div className="text-2xl font-extrabold">{repData.visits_30}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-blue-200">Orders</div><div className="text-2xl font-extrabold">{repData.orders_count_30}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-blue-200">Sales</div><div className="text-2xl font-extrabold">{fmtMoney(repData.sales_30)}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-blue-200">Collections</div><div className="text-2xl font-extrabold">{fmtMoney(repData.collections_30)}</div></div>
          </div>
        </Card>
      )}
    </div>
  );
}
